import { useCallback, useEffect, memo, useRef, useMemo } from "react";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { ListNode, ListItemNode } from "@lexical/list";
import { LinkNode } from "@lexical/link";
import { ImageNode } from "./lexical/nodes/ImageNode";
import { VideoNode } from "./lexical/nodes/VideoNode";
import { YouTubeNode } from "./lexical/nodes/YouTubeNode";
import ToolbarPlugin from "./lexical/plugins/ToolbarPlugin";

const EditorStateUpdater = memo(({ initialJSON }) => {
  const [editor] = useLexicalComposerContext();
  const previousJSONRef = useRef(null);
  const isUpdatingRef = useRef(false);

  useEffect(() => {
    if (
      !initialJSON ||
      typeof initialJSON !== "object" ||
      isUpdatingRef.current
    ) {
      return;
    }

    const currentJSONString = JSON.stringify(initialJSON);

    if (previousJSONRef.current === currentJSONString) {
      return;
    }

    previousJSONRef.current = currentJSONString;
    isUpdatingRef.current = true;

    try {
      const currentState = editor.getEditorState();
      const currentJSON = currentState.toJSON();
      const currentStateString = JSON.stringify(currentJSON);

      if (currentStateString !== currentJSONString) {
        editor.update(() => {
          const editorState = editor.parseEditorState(currentJSONString);
          editor.setEditorState(editorState);
        });
      }
    } catch (error) {
    } finally {
      setTimeout(() => {
        isUpdatingRef.current = false;
      }, 50);
    }
  }, [editor, initialJSON]);

  return null;
});

const IsolatedLexicalEditor = memo(
  ({
    initialJSON,
    onChange,
    placeholder = "Start typing…",
    editorKey,
    readOnly = false,
  }) => {
    const onChangeRef = useRef(onChange);
    const changeTimerRef = useRef(null);

    useEffect(() => {
      onChangeRef.current = onChange;
    }, [onChange]);

    const initialConfig = useMemo(() => {
      const config = {
        namespace: `isolated-${editorKey}`,
        nodes: [
          HeadingNode,
          QuoteNode,
          ListNode,
          ListItemNode,
          LinkNode,
          ImageNode,
          VideoNode,
          YouTubeNode,
        ],
        editable: !readOnly,
        onError(error) {},
        theme: {
          paragraph: "mb-2",
          heading: {
            h1: "text-2xl font-bold mb-4",
            h2: "text-xl font-semibold mb-3",
            h3: "text-lg font-semibold mb-2",
          },
          list: {
            nested: {
              listitem: "ml-4",
            },
            ol: "list-decimal ml-4",
            ul: "list-disc ml-4",
          },
          link: "text-blue-600 hover:underline",
        },
      };

      if (initialJSON && typeof initialJSON === "object") {
        try {
          config.editorState = JSON.stringify(initialJSON);
        } catch (error) {}
      }

      return config;
    }, [editorKey, readOnly]);

    const handleChange = useCallback(
      (editorState) => {
        if (readOnly) return;

        if (changeTimerRef.current) {
          clearTimeout(changeTimerRef.current);
        }

        changeTimerRef.current = setTimeout(() => {
          editorState.read(() => {
            const json = editorState.toJSON();
            if (onChangeRef.current) {
              onChangeRef.current(json);
            }
          });
        }, 100);
      },
      [readOnly]
    );

    useEffect(() => {
      return () => {
        if (changeTimerRef.current) {
          clearTimeout(changeTimerRef.current);
        }
      };
    }, []);

    return (
      <LexicalComposer key={editorKey} initialConfig={initialConfig}>
        <div
          className={`border rounded-md overflow-hidden ${
            readOnly ? "bg-gray-50" : ""
          }`}
        >
          {!readOnly && <ToolbarPlugin />}
          <div className="p-3 relative">
            <RichTextPlugin
              contentEditable={
                <ContentEditable className="min-h-[160px] outline-none prose prose-sm max-w-none" />
              }
              placeholder={
                !readOnly ? (
                  <div className="text-gray-400 absolute top-0 left-0 pointer-events-none">
                    {placeholder}
                  </div>
                ) : null
              }
              ErrorBoundary={LexicalErrorBoundary}
            />
          </div>
          <HistoryPlugin />
          <ListPlugin />
          <LinkPlugin />
          {!readOnly && <OnChangePlugin onChange={handleChange} />}
          <EditorStateUpdater initialJSON={initialJSON} />
        </div>
      </LexicalComposer>
    );
  },
  (prevProps, nextProps) => {
    if (prevProps.editorKey !== nextProps.editorKey) return false;
    if (prevProps.readOnly !== nextProps.readOnly) return false;
    return JSON.stringify(prevProps.initialJSON) === JSON.stringify(nextProps.initialJSON);
  }
);

IsolatedLexicalEditor.displayName = "IsolatedLexicalEditor";

export default IsolatedLexicalEditor;
